import { useEffect, useMemo, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { SelectList, SelectListItem } from "@/components/ui/select-list";
import { ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import {
  AlertCircleIcon,
  CopyIcon,
  ExternalLinkIcon,
  MailIcon,
  MailPlusIcon,
  MoreVerticalIcon,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
  Trash2Icon,
} from "lucide-react";
import { toast } from "sonner";
import gmail from "@/assets/gmail.svg";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TruncatedText } from "@/components/ui/truncated-text";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Frame, FrameAction, FrameCount, FrameHeader, FramePanel, FrameTitle } from "@/components/ui/frame";
import { useCampaignStore, useCreatorsStore } from "@/stores";
import { useEveActionsStore } from "@/stores/eve/eveActionsStore";
import { useIntegrationsStore } from "@/stores/eve/integrationsStore";
import type { CampaignMailbox } from "@/types/types";

type MailboxChange = Parameters<ReturnType<typeof useEveActionsStore.getState>["setCampaignMailbox"]>[1];

/** What one mailbox has done on this campaign, counted from the creators stamped with its address. */
interface MailboxStats {
  /** Cold-emailed (past to-contact / pending-outreach). */
  emailed: number;
  /** Emailed and answered — past "outreached", like the campaign's analytics bar. */
  replied: number;
  /** Creator threads it holds — Amy refuses to remove it while any exist. */
  threads: number;
}

const NOT_EMAILED = ["pending", "to-contact", "pending-outreach"];

/**
 * Amy-run campaigns (Settings → Email, below Email Limits): the pool
 * mailboxes attached to the campaign (`eve_mailboxes`, no primary), one
 * table row each with what it has done here. Every mailbox that is sending
 * sends cold emails and follow-ups, each under the campaign's per-mailbox
 * limits; a stopped one sends nothing unsolicited but keeps answering the
 * creators who replied. Everything else lives in the row menu (⋮ or a
 * right-click): stop / start cold emails, re-queue the creators it emailed
 * who never answered (a fresh cold email from the other mailboxes — the
 * re-queue drops their stamp, so they leave its counts), copy the address,
 * open its Gmail, and remove it (refused while it holds creator threads).
 * Add Mailbox attaches another from the org's ACTIVE Gmail pool (Settings →
 * Integrations). Every change rides the set-campaign-mailbox action route —
 * the same executor as the chat tool.
 */
export function AmyMailboxFrame() {
  const { campaign: c, campaignId } = useCampaignStore();
  const { creators } = useCreatorsStore();
  const { getToken, orgId } = useAuth();
  const { connections, loaded, loadError, fetchIntegrations } = useIntegrationsStore();
  const { setCampaignMailbox, isSavingConfig } = useEveActionsStore();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  /** The re-queue prompt: which switched-off mailbox, and how many creators it would move. */
  const [requeue, setRequeue] = useState<{ mailbox: CampaignMailbox; count: number } | null>(null);
  /** The row whose change is in flight (its menu button spins). */
  const [pendingId, setPendingId] = useState<string | null>(null);

  const mailboxes: CampaignMailbox[] = c?.eve_mailboxes ?? [];
  const pool = connections.filter((x) => x.toolkit === "gmail" && x.status === "ACTIVE");
  // The same address can be in the pool twice (Composio-managed AND
  // Uplodio-managed sign-in), so rows are keyed by account id and, when the
  // pool mixes sign-in apps, say which one each row is.
  const mixedAuth = new Set(pool.map((m) => m.auth_mode).filter(Boolean)).size > 1;
  const needle = query.trim().toLowerCase();
  const filteredPool = needle ? pool.filter((m) => (m.account_email ?? m.connected_account_id).toLowerCase().includes(needle)) : pool;

  const stats = useMemo(() => {
    const byEmail = new Map<string, MailboxStats>();
    for (const cr of creators) {
      const email = cr.eve_email_address?.toLowerCase();
      if (!email) continue;
      const row = byEmail.get(email) ?? { emailed: 0, replied: 0, threads: 0 };
      if (cr.thread_id) row.threads += 1;
      if (!NOT_EMAILED.includes(cr.status ?? "pending")) {
        row.emailed += 1;
        if (cr.status !== "outreached") row.replied += 1;
      }
      byEmail.set(email, row);
    }
    return byEmail;
  }, [creators]);

  // Pool is fetched when the picker opens (org-scoped via the token).
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setQuery("");
      return;
    }
    void (async () => {
      const token = await getToken();
      if (token) await fetchIntegrations(token);
    })();
  }, [open, getToken, fetchIntegrations]);

  const change = async (body: MailboxChange, successMessage: string) => {
    if (!campaignId || !orgId) return null;
    const token = await getToken();
    if (!token) return null;
    setPendingId(body.mailbox ?? null);
    try {
      return await setCampaignMailbox(campaignId, body, orgId, token, successMessage);
    } finally {
      setPendingId(null);
    }
  };

  const handleConfirm = async () => {
    if (selected && (await change({ mailbox: selected }, "Mailbox added."))) setOpen(false);
  };

  /** Switch off (or re-check a switched-off mailbox) and offer the re-queue when creators are waiting. */
  const switchOff = async (m: CampaignMailbox, successMessage: string) => {
    const result = await change({ mailbox: m.account_id, enabled: false }, successMessage);
    if (!result) return;
    if ((result.unreplied_count ?? 0) > 0) setRequeue({ mailbox: m, count: result.unreplied_count ?? 0 });
    else if (m.paused) toast.info("No unanswered creators on this mailbox.");
  };

  const handleRequeue = async () => {
    if (!requeue) return;
    const result = await change(
      { mailbox: requeue.mailbox.account_id, enabled: false, requeue_unreplied: true },
      "Creators re-queued."
    );
    if (result) setRequeue(null);
  };

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>
          Amy's Mailboxes <FrameCount>{mailboxes.length}</FrameCount>
        </FrameTitle>
        <FrameAction>
          <Button size="sm" variant="secondary" disabled={isSavingConfig} onClick={() => setOpen(true)}>
            <MailPlusIcon />
            Add Mailbox
          </Button>
        </FrameAction>
      </FrameHeader>
      {mailboxes.length === 0 ? (
        <FramePanel className="flex items-center gap-2 p-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md border bg-muted/40">
            <img src={gmail} alt="" className="size-4.5 object-contain" />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <p className="truncate text-sm font-medium">No mailbox attached yet</p>
            <p className="text-muted-foreground text-xs">
              Amy uses the org's only connected Gmail automatically; with several connected, add one before outreach.
            </p>
          </div>
        </FramePanel>
      ) : (
        <Table variant="card">
          <TableHeader>
            <TableRow>
              <TableHead className="w-full">Mailbox</TableHead>
              <TableHead className="w-32">Status</TableHead>
              <TableHead className="w-20">Emailed</TableHead>
              <TableHead className="w-28">Replied</TableHead>
              <TableHead className="w-20">No reply</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {mailboxes.map((m) => (
              <MailboxRow
                key={m.account_id}
                mailbox={m}
                stats={(m.email && stats.get(m.email.toLowerCase())) || { emailed: 0, replied: 0, threads: 0 }}
                busy={isSavingConfig}
                pending={pendingId === m.account_id}
                onStart={() => void change({ mailbox: m.account_id, enabled: true }, "Mailbox switched on.")}
                onStop={() => void switchOff(m, "Mailbox switched off — it keeps answering the creators who replied.")}
                onRequeue={() => void switchOff(m, "Mailbox checked.")}
                onRemove={() => void change({ mailbox: m.account_id, remove: true }, "Mailbox removed.")}
              />
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Add a mailbox</AlertDialogTitle>
            <AlertDialogDescription>
              Every mailbox switched on sends cold emails and follow-ups, each
              within the limits above. Each creator is emailed from one mailbox
              for good. Connect more mailboxes in{" "}
              <Link
                to="/dashboard/settings/integrations"
                className="underline underline-offset-2 hover:text-foreground"
              >
                Settings → Integrations
              </Link>
              .
            </AlertDialogDescription>
          </AlertDialogHeader>

          {!loaded && !loadError && <Skeleton className="h-14 w-full rounded-lg" />}
          {loadError && !loaded && (
            <Alert variant="warning">
              <AlertTitle>
                <AlertCircleIcon className="size-4" />
                Couldn't load the mailbox pool
              </AlertTitle>
              <AlertDescription>Amy didn't answer — close and try again.</AlertDescription>
            </Alert>
          )}
          {loaded && pool.length === 0 && (
            <Alert variant="warning">
              <AlertTitle>
                <AlertCircleIcon className="size-4" />
                No Gmail connected
              </AlertTitle>
              <AlertDescription>
                Connect a Gmail account in Settings → Integrations first — Amy
                has nothing to send from.
              </AlertDescription>
            </Alert>
          )}
          {loaded && pool.length > 0 && (
            <SelectList
              value={selected ?? ""}
              onValueChange={setSelected}
              disabled={isSavingConfig}
              className="max-h-100"
              search={{ value: query, onValueChange: setQuery, placeholder: "Search mailboxes" }}
            >
              {filteredPool.length === 0 && (
                <p className="px-2 py-3 text-sm text-muted-foreground">No mailbox matches "{query.trim()}".</p>
              )}
              {filteredPool.map((m) => {
                const id = m.connected_account_id;
                const attached = mailboxes.some(
                  (x) => x.account_id === id || (!!x.email && !!m.account_email && x.email.toLowerCase() === m.account_email.toLowerCase())
                );
                const usage = m.used_by_campaigns.length
                  ? `Sends for ${m.used_by_campaigns.length} campaign${m.used_by_campaigns.length === 1 ? "" : "s"}`
                  : "Not used by any campaign yet";
                const via = mixedAuth && m.auth_mode ? ` · via ${m.auth_mode === "uplodio" ? "Uplodio" : "Composio"}` : "";

                return (
                  <SelectListItem key={id} value={id} disabled={attached}>
                    <ItemMedia variant="icon">
                      <img src={gmail} alt="" className="size-4 object-contain" />
                    </ItemMedia>
                    <ItemContent className="min-w-0 gap-0">
                      <ItemTitle className="truncate">{m.account_email ?? id}</ItemTitle>
                      <ItemDescription>
                        {usage}
                        {via}
                      </ItemDescription>
                    </ItemContent>
                    {attached && <Badge variant="flat">On this campaign</Badge>}
                  </SelectListItem>
                );
              })}
            </SelectList>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSavingConfig}>Cancel</AlertDialogCancel>
            <Button onClick={handleConfirm} disabled={!selected || isSavingConfig}>
              {isSavingConfig ? <Spinner /> : <MailIcon />}
              Add Mailbox
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={requeue !== null} onOpenChange={(next) => !next && setRequeue(null)}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Re-queue unanswered creators?</AlertDialogTitle>
            <AlertDialogDescription>
              {requeue?.count ?? 0} creator{(requeue?.count ?? 0) === 1 ? "" : "s"} got a cold email from{" "}
              {requeue?.mailbox.email ?? requeue?.mailbox.account_id} and never answered. Switched off, this
              mailbox sends them no more follow-ups. Re-queue them and your other mailboxes send them a fresh
              cold email. A reply to the old email after that is not picked up by Amy (you'd see it in that Gmail).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSavingConfig}>Keep waiting</AlertDialogCancel>
            <Button onClick={() => void handleRequeue()} disabled={isSavingConfig}>
              {isSavingConfig ? <Spinner /> : <RotateCcwIcon />}
              Re-queue {requeue?.count ?? 0}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Frame>
  );
}

/** One mailbox: what it has done here, and every action on it in the ⋮ / right-click menu. */
function MailboxRow({
  mailbox: m,
  stats,
  busy,
  pending,
  onStart,
  onStop,
  onRequeue,
  onRemove,
}: {
  mailbox: CampaignMailbox;
  stats: MailboxStats;
  busy: boolean;
  pending: boolean;
  onStart: () => void;
  onStop: () => void;
  onRequeue: () => void;
  onRemove: () => void;
}) {
  const label = m.email ?? m.account_id;
  const on = !m.paused;
  const noReply = stats.emailed - stats.replied;

  return (
    <TableRow data-context-menu>
      {/* max-w-0: the column takes what's left, so a long address truncates. */}
      <TableCell className="max-w-0 font-medium">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted">
            <img src={gmail} alt="" className="size-3.5 object-contain" />
          </div>
          <TruncatedText className="leading-normal">{label}</TruncatedText>
        </div>
      </TableCell>
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant={on ? "success" : "muted"}>{on ? "Sending" : "Replies only"}</Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-60 text-center text-balance">
            {on
              ? "Sends cold emails and follow-ups, within the limits above."
              : "No cold emails or follow-ups; still answers the creators who replied."}
          </TooltipContent>
        </Tooltip>
      </TableCell>
      {/* A mailbox that has emailed nobody here reads "—", not zeros. */}
      <TableCell className="font-mono text-xs text-muted-foreground">{stats.emailed || "—"}</TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {stats.emailed > 0 ? (
          <>
            {stats.replied}
            <span className="ms-1.5 text-foreground">{Math.round((stats.replied / stats.emailed) * 100)}%</span>
          </>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">{stats.emailed > 0 ? noReply : "—"}</TableCell>
      <TableCell>
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="muted-ghost" size="icon-sm" aria-label={`Options for ${label}`} disabled={busy}>
                {pending ? <Spinner className="size-3.5" /> : <MoreVerticalIcon />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56">
              {on ? (
                <DropdownMenuItem onSelect={onStop}>
                  <PauseIcon />
                  Stop cold emails
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onSelect={onStart}>
                    <PlayIcon />
                    Start cold emails
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={onRequeue}>
                    <RotateCcwIcon />
                    Re-queue no-replies{noReply > 0 ? ` (${noReply})` : ""}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              {m.email && (
                <>
                  <DropdownMenuItem
                    onSelect={() => {
                      void navigator.clipboard.writeText(m.email!);
                      toast.success("Address copied.");
                    }}
                  >
                    <CopyIcon />
                    Copy address
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      href={`https://mail.google.com/mail/?authuser=${encodeURIComponent(m.email)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLinkIcon />
                      Open in Gmail
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {stats.threads > 0 && (
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  Holds {stats.threads} creator thread{stats.threads === 1 ? "" : "s"}, so it can't be removed
                </DropdownMenuLabel>
              )}
              <DropdownMenuItem variant="destructive" disabled={stats.threads > 0} onSelect={onRemove}>
                <Trash2Icon />
                Remove from campaign
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
